// src/admin/MasterLogin.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
    masterLogin,
    selectIsAuthenticated,
    selectLoading,
    selectError,
    selectSuccess,
    clearError,
    checkSessionStorageAuth  
} from '../_store/masterAuthSlice';
import UAVSphere_logo from "../assets/images/UAVspherelogo.png";
import Powered_by_logo from "../assets/images/Powered_by_logo.png";

export const MasterLogin = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const loading = useSelector(selectLoading);
    const error = useSelector(selectError);
    const success = useSelector(selectSuccess);
    
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    
    useEffect(() => {
        // Check if already authenticated
        dispatch(checkSessionStorageAuth  ());
    }, [dispatch]);
    
    useEffect(() => {
        // Redirect to SuperAdmin if authenticated
        if (isAuthenticated || success) {
            const timer = setTimeout(() => {
                navigate('/superadmin');  // ✅ Navigate to SuperAdmin page
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isAuthenticated, success, navigate]);
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!password.trim()) {
            return;
        }
        
        dispatch(clearError());
        const result = await dispatch(masterLogin(password));
        
        // If login successful, navigate to SuperAdmin
        if (masterLogin.fulfilled.match(result)) {
            navigate('/superadmin');
        }
    };
    
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !loading) {
            handleSubmit(e);
        }
    };
    
    const styles = {
        container: {
            minHeight: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            padding: '1rem',
            position: 'relative'
        },
        backgroundOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.1
        },
        loginCard: {
            background: 'white',
            borderRadius: '20px',
            padding: '3rem',
            maxWidth: '450px',
            width: '100%',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            position: 'relative',
            zIndex: 1,
            animation: 'fadeInUp 0.6s ease-out'
        },
        logoContainer: {
            textAlign: 'center',
            marginBottom: '2rem'
        },
        logo: {
            width: '180px',
            height: 'auto',
            marginBottom: '1rem'
        },
        title: {
            fontSize: '2rem',
            fontWeight: '700',
            color: '#1e3c72',
            marginBottom: '0.5rem',
            textAlign: 'center'
        },
        subtitle: {
            color: '#666',
            fontSize: '0.95rem',
            textAlign: 'center',
            marginBottom: '2rem',
            lineHeight: '1.5'
        },
        form: {
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem'
        },
        inputGroup: {
            position: 'relative'
        },
        label: {
            display: 'block',
            marginBottom: '0.5rem',
            color: '#333',
            fontWeight: '500',
            fontSize: '0.9rem'
        },
        passwordInputWrapper: {
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
        },
        input: {
            width: '100%',
            padding: '14px 50px 14px 16px',
            border: '2px solid #e1e5e9',
            borderRadius: '10px',
            fontSize: '1rem',
            transition: 'all 0.3s',
            boxSizing: 'border-box',
            outline: 'none',
            background: '#fafbfc'
        },
        eyeButton: {
            position: 'absolute',
            right: '12px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1.2rem',
            padding: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            transition: 'color 0.2s'
        },
        loginButton: {
            width: '100%',
            padding: '14px',
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '1.1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s',
            marginTop: '1rem',
            boxShadow: '0 4px 15px rgba(42, 82, 152, 0.3)'
        },
        loginButtonDisabled: {
            opacity: 0.7,
            cursor: 'not-allowed'
        },
        errorMessage: {
            background: '#fee',
            color: '#c33',
            padding: '12px',
            borderRadius: '8px',
            fontSize: '0.9rem',
            textAlign: 'center',
            marginBottom: '1rem',
            border: '1px solid #fcc'
        },
        successMessage: {
            background: '#efe',
            color: '#3a3',
            padding: '12px',
            borderRadius: '8px',
            fontSize: '0.9rem',
            textAlign: 'center',
            marginBottom: '1rem',
            border: '1px solid #cfc'
        },
        loader: {
            display: 'inline-block',
            width: '20px',
            height: '20px',
            border: '3px solid rgba(255,255,255,0.3)',
            borderRadius: '50%',
            borderTopColor: 'white',
            animation: 'spin 1s ease-in-out infinite',
            marginRight: '8px'
        },
        backButton: {
            width: '100%',
            padding: '12px',
            background: 'transparent',
            color: '#666',
            border: '1px solid #ddd',
            borderRadius: '10px',
            fontSize: '0.95rem',
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginTop: '1rem'
        },
        footer: {
            textAlign: 'center',
            marginTop: '2rem'
        },
        footerText: {
            color: '#999',
            fontSize: '0.8rem'
        },
        poweredBy: {
            textAlign: 'center',
            marginTop: '2rem',
            paddingTop: '1rem',
            borderTop: '1px solid #eee'
        },
        poweredByText: {
            fontSize: '0.75rem',
            color: '#999'
        },
        lockIcon: {
            fontSize: '3rem',
            marginBottom: '1rem',
            textAlign: 'center'
        }
    };
    
    // Add animation keyframes
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
        @keyframes fadeInUp {
            from {
                opacity: 0;
                transform: translateY(30px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
        @keyframes spin {
            to { transform: rotate(360deg); }
        }
    `;
    document.head.appendChild(styleSheet);
    
    return (
        <div style={styles.container}>
            <div style={styles.backgroundOverlay}></div>
            
            <div style={styles.loginCard}>
                <div style={styles.logoContainer}>
                    <div style={styles.lockIcon}>🔐</div>
                    <img src={UAVSphere_logo} alt="UAVSphere Logo" style={styles.logo} />
                </div>
                
                <h2 style={styles.title}>Admin Access</h2>
                <p style={styles.subtitle}>
                    This area is restricted to authorized personnel only.
                    Please enter the master password to continue.
                </p>
                
                {error && (
                    <div style={styles.errorMessage}>
                        ⚠️ {error}
                    </div>
                )}
                
                {success && (
                    <div style={styles.successMessage}>
                        ✅ Login successful! Redirecting to Super Admin...
                    </div>
                )}
                
                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.inputGroup}>
                        <label style={styles.label}>Master Password</label>
                        <div style={styles.passwordInputWrapper}>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Enter master password"
                                style={styles.input}
                                disabled={loading}
                                autoFocus
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#2a5298';
                                    e.target.style.background = 'white';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(42, 82, 152, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e1e5e9';
                                    e.target.style.background = '#fafbfc';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                            <button
                                type="button"
                                style={styles.eyeButton}
                                onClick={() => setShowPassword(!showPassword)}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#333'}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#999'}
                            >
                                {showPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                    </div>
                    
                    <button
                        type="submit"
                        style={{
                            ...styles.loginButton,
                            ...(loading ? styles.loginButtonDisabled : {})
                        }}
                        disabled={loading}
                        onMouseEnter={(e) => {
                            if (!loading) {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(42, 82, 152, 0.4)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!loading) {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 15px rgba(42, 82, 152, 0.3)';
                            }
                        }}
                    >
                        {loading ? (
                            <>
                                <span style={styles.loader}></span>
                                Verifying...
                            </>
                        ) : (
                            'Access Admin Panel'
                        )}
                    </button>
                    
                    <button
                        type="button"
                        style={styles.backButton}
                        onClick={() => navigate('/')}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f5f5f5';
                            e.currentTarget.style.borderColor = '#ccc';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.borderColor = '#ddd';
                        }}
                    >
                        ← Back to Home
                    </button>
                </form>
                
                <div style={styles.footer}>
                    <p style={styles.footerText}>
                        Protected by master password authentication
                    </p>
                </div>
                
                <div style={styles.poweredBy}>
                    <p style={styles.poweredByText}>
                        Powered by GeoWGS84 <br/>
                        <a href="https://www.geowgs84.com/" target="_blank" rel="noopener noreferrer">
                            <img src={Powered_by_logo} alt="GeoWGS84 Logo" style={{ width: '25px', height: 'auto', marginTop: '5px' }} />
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default MasterLogin;
